import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Link as DSLink,
  Loader,
  Menu,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useGetWidget } from '../../../features/ViolationsTab/hooks';
import {
  CF_CRT_MED,
  CF_INF_MED,
  CF_NEU_BLD,
  CF_NEU_STL,
} from '../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';
import {
  type ActiveWidgetItem,
  type WidgetDataListItem,
  type WidgetMenuItem,
} from '../../types';

import SodWidgetListItem from './SodWidgetListItem';

import './SodWidget.css';

const ALLOWED_COLUMN_SPANS = [3, 4, 6, 8, 9] as const;

export const WIDGET_KINDS: Record<string, string> = {
  REGULAR: 'regular',
  USER: 'user',
  ENDPOINT: 'endpoint',
  ENTITLEMENT: 'entitlement',
  TABBED_METRICS: 'tabbedMetrics',
  COMPARISON_METRIC: 'comparisonMetric',
} as const;

const WIDGET_ACTION: Record<
  string,
  {
    label: string;
    value: string;
    icon: string;
  }
> = {
  HIDE: {
    label: 'Hide',
    value: 'HIDE',
    icon: 'VisibilityOff',
  },
  CUSTOMIZE: {
    label: 'Customize',
    value: 'CUSTOMIZE',
    icon: 'Settings',
  },
} as const;

type AllowedSpan = (typeof ALLOWED_COLUMN_SPANS)[number];
type WidgetAction = (typeof WIDGET_ACTION)[keyof typeof WIDGET_ACTION];

interface SodWidgetProps {
  kind: string;
  widgetSource: WidgetMenuItem;
  currentTab: string | null;
  onAction?: (action: string, value: string) => void;
  customWidthSpan?: AllowedSpan;
  handleClick?: (activeItem: ActiveWidgetItem) => void;
  onTabChange?: (tab: string) => void;
  totalAmount?: number;
  totalLabel?: string;
  tabs?: { label: string; value: string }[];
  defaultTab?: string;
}

/**
 * Configurable widget that fetches and displays a list of items
 * (violations, users, endpoints, software metrics, or benchmarks) with loading,
 * error, and empty states. Supports tabbed filtering, custom column spans, and
 * contextual menu actions (hide/customize).
 *
 * @param {SodWidgetProps} props
 * @param {string} props.kind - Widget kind determining list-item rendering style (e.g. 'regular', 'tabbedMetrics').
 * @param {WidgetMenuItem} props.widgetSource - Configuration object with endpoint, label, icon, and value for the widget.
 * @param {string | null} props.currentTab - Currently active dashboard tab context passed to list items.
 * @param {(action: string, value: string) => void} [props.onAction] - Callback fired when a menu action (Hide/Customize) is selected.
 * @param {AllowedSpan} [props.customWidthSpan] - Optional column span override (3, 4, 6, 8, or 9).
 * @param {(activeItem: ActiveWidgetItem) => void} [props.handleClick] - Callback fired when a list item is clicked.
 * @param {(tab: string) => void} [props.onTabChange] - Callback fired when tab selection changes (TABBED_METRICS kind).
 * @param {number} [props.totalAmount] - Total amount displayed above tabs (TABBED_METRICS kind).
 * @param {string} [props.totalLabel] - Label for the total amount section.
 * @param {{ label: string; value: string }[]} [props.tabs] - Custom tab definitions; defaults to Expired/Upcoming.
 * @param {string} [props.defaultTab] - Default active tab value on mount.
 * @returns {ReactElement} The rendered widget with header, optional tabs, content list, and footer.
 */
function SodWidget({
  kind,
  widgetSource,
  currentTab,
  onAction = undefined,
  customWidthSpan = undefined,
  handleClick = undefined,
  onTabChange = undefined,
  totalAmount = undefined,
  totalLabel = undefined,
  tabs = undefined,
  defaultTab = undefined,
}: SodWidgetProps): ReactElement {
  const l = useInvertedLocalize();
  const { isGlobalLoading } = useTypedSelector((state) => state.aag.global);

  const [data, setData] = useState<WidgetDataListItem[] | null>(null);
  const [activeTab, setActiveTab] = useState<string>(
    defaultTab ?? tabs?.[0]?.value ?? 'expired'
  );

  const { handleFetchWidgets, isLoading, isError } = useGetWidget();

  const triggerHandleFetchWidgets = useCallback(() => {
    if (widgetSource.endpoint) {
      setData(null);

      const endpoint =
        kind === WIDGET_KINDS.TABBED_METRICS
          ? `${widgetSource.endpoint}?tab=${activeTab}`
          : widgetSource.endpoint;

      handleFetchWidgets(endpoint)
        .then((response) => {
          const typedResponse = response as {
            widgetName: string;
            top5Objects?: WidgetDataListItem[];
          };

          setData(typedResponse.top5Objects ?? []);
        })
        .catch(() => {
          Logger.log('Error loading the widget', widgetSource.label);

          setData(null);
        });
    }
  }, [
    handleFetchWidgets,
    widgetSource.endpoint,
    widgetSource.label,
    kind,
    activeTab,
  ]);

  useEffect(() => {
    if (!isGlobalLoading) {
      triggerHandleFetchWidgets();
    }
  }, [isGlobalLoading, triggerHandleFetchWidgets]);

  const wrapperClasses = cx(
    'Sod-Widget-wrapper',
    `Sod-Widget-wrapper-${kind}`,
    customWidthSpan && `Sod-Widget-wrapper-${customWidthSpan}`
  );

  const renderContentBody = useCallback(() => {
    if (isError) {
      return (
        <Box tag='div' className='Sod-Widget-errorWrapper'>
          <Box tag='div' className='Sod-Widget-errorMessage'>
            <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
            <Typography kind='body2' color={CF_CRT_MED}>
              {l("Data couldn't be retrieved.")}
            </Typography>
          </Box>
          <Button
            kind='filled'
            size='small'
            onClick={() => triggerHandleFetchWidgets()}>
            {l('Refresh')}
          </Button>
        </Box>
      );
    }

    if (isLoading || data === null) {
      return (
        <Box tag='div' className='Sod-Widget-loadingWrapper'>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    if (Array.isArray(data) && data.length === 0) {
      return (
        <Box tag='div' className='Sod-Widget-noDataWrapper'>
          <Box tag='div' className='Sod-Widget-errorMessage'>
            <Icon kind='AlertInfo' size='large' color={CF_INF_MED} />
            <Typography kind='body2' color={CF_NEU_BLD}>
              {l('Data not found')}
            </Typography>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Try updating the filters or check back later.')}
            </Typography>
          </Box>
        </Box>
      );
    }

    return data.map((item) => (
      <SodWidgetListItem
        key={item.id}
        kind={widgetSource.kind}
        tab={currentTab}
        iconString={
          // eslint-disable-next-line no-nested-ternary -- readable sequential condition mapping
          widgetSource.kind === WIDGET_KINDS.ENDPOINT ||
          widgetSource.kind === WIDGET_KINDS.TABBED_METRICS
            ? item.additionalInfo?.applicationLogo
            : // eslint-disable-next-line no-nested-ternary
            widgetSource.kind === WIDGET_KINDS.COMPARISON_METRIC
            ? undefined
            : widgetSource.kind === WIDGET_KINDS.USER
            ? item.name
            : widgetSource.icon
        }
        text={item.name}
        count={item.count}
        additionalInfo={item.additionalInfo}
        onClick={() => {
          if (handleClick) {
            handleClick({ value: widgetSource.value, data: item });
          }
        }}
      />
    ));
  }, [
    l,
    isError,
    isLoading,
    data,
    currentTab,
    widgetSource.icon,
    widgetSource.kind,
    widgetSource.value,
    handleClick,
    triggerHandleFetchWidgets,
  ]);

  return (
    <Box tag='div' className={wrapperClasses}>
      <Box className='Sod-Widget-headerWrapper'>
        <Box tag='div' className='Sod-Widget-headerTitleWrapper'>
          <Typography
            kind='h2'
            lineLimit={1}
            color={CF_NEU_BLD}
            showTooltipOnHover={false}
            className='Sod-Widget-headerTitle'>
            {widgetSource.label}
          </Typography>
        </Box>
        <Box className='Sod-Widget-hoverables'>
          <Menu
            options={Object.values(WIDGET_ACTION).map((action) => ({
              ...action,
              label: l(action.label),
            }))}
            onChange={(config: WidgetAction) => {
              if (onAction) {
                onAction(config.value, widgetSource.value);
              }
            }}
            placement='bottom-end'
            trigger={
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('more')}
                icon={
                  <Icon kind='MoreVertical' color='neutral-100' size='medium' />
                }
              />
            }
          />
        </Box>
      </Box>

      {/* TABBED_METRICS: Total Amount Section */}
      {kind === WIDGET_KINDS.TABBED_METRICS && totalAmount !== undefined && (
        <Box tag='div' className='Sod-Widget-totalAmountSection'>
          <Typography
            kind='h1'
            color={CF_NEU_BLD}
            className='Sod-Widget-totalAmount'>
            {l(`$${totalAmount.toLocaleString()}`)}
          </Typography>
          <Typography kind='body2' color={CF_NEU_STL}>
            {totalLabel ?? l('Total at Risk')}
          </Typography>
        </Box>
      )}

      {/* TABBED_METRICS: Tab Buttons */}
      {kind === WIDGET_KINDS.TABBED_METRICS && (
        <Box tag='div' className='Sod-Widget-tabsContainer'>
          {tabs && tabs.length > 0 ? (
            tabs.map((tab) => (
              <button
                key={tab.value}
                type='button'
                className={cx('Sod-Widget-tab', {
                  'Sod-Widget-tab--active': activeTab === tab.value,
                })}
                onClick={() => {
                  setActiveTab(tab.value);

                  if (onTabChange) {
                    onTabChange(tab.value);
                  }
                }}>
                {l(tab.label)}
              </button>
            ))
          ) : (
            <>
              <button
                type='button'
                className={cx('Sod-Widget-tab', {
                  'Sod-Widget-tab--active': activeTab === 'expired',
                })}
                onClick={() => {
                  setActiveTab('expired');

                  if (onTabChange) {
                    onTabChange('expired');
                  }
                }}>
                {l('Expired')}
              </button>
              <button
                type='button'
                className={cx('Sod-Widget-tab', {
                  'Sod-Widget-tab--active': activeTab === 'upcoming',
                })}
                onClick={() => {
                  setActiveTab('upcoming');

                  if (onTabChange) {
                    onTabChange('upcoming');
                  }
                }}>
                {l('Upcoming')}
              </button>
            </>
          )}
        </Box>
      )}
      <Box tag='div' className='Sod-Widget-contentWrapper'>
        {renderContentBody()}
      </Box>
      <Box tag='div' className='Sod-Widget-footerWrapper'>
        <DSLink
          text='View All'
          kind='button'
          onClick={() => {
            Logger.log('View all pressed');
          }}
          suffixIconKind='ArrowRight'
          size='medium'
        />
      </Box>
    </Box>
  );
}

SodWidget.defaultProps = {
  onAction: undefined,
  customWidthSpan: undefined,
  handleClick: undefined,
  onTabChange: undefined,
  totalAmount: undefined,
  totalLabel: undefined,
  tabs: undefined,
  defaultTab: undefined,
};

export default memo(SodWidget);
